/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/PeoplePicker" />
import { ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListSelection } from "VSSUI/List";
import { css } from "VSSUI/Util";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { BoardFilterDataSource } from "Wit/Common/Controls/Boards/BoardFilterDataSource";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WiqlOperators } from "Wit/Common/WiqlOperators";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import {
    convertPotentialIdentityRefFromFieldValue,
    getFriendlyDistinctDisplayName,
    isWorkItemIdentityRef
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { SettingsConstants } from "Wit/SprintsHub/Common/Constants";
import { ITaskboardWorkItem } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { IdentityView } from "Wit/UI/Components/IdentityView/IdentityView";
import { IdentityDisplayMode } from "Wit/UI/Enums";
import * as React from "react";
import { ITaskboardService } from "../../Services/ITaskboardService";
import { ITaskboardRow } from "./ITaskboardRow";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

const MeFilterItem: IListBoxItem<IdentityRef> = {
    id: WiqlOperators.MacroMe,
    text: `${WiqlOperators.MacroStart}${Resources.Wiql_MacroMe}`
};

const UnassignedFilterItem: IListBoxItem<IdentityRef> = {
    id: "_Unassigned_",
    text: Resources.AssignedToEmptyText
};

const AllFilterItem: IListBoxItem<IdentityRef> = {
    id: "_All_",
    text: Resources.AllText
};

const peopleSetting = "Sprints/taskboard/people-switcher/Filter";

interface IPeoplePickerProps {
    items: ObservableArray<IMenuItem>;
    selection: IListSelection;
}

export class PeoplePicker extends VssComponent<IPeoplePickerProps> {
    public static getPeoplePickerMenuItem(
        pageContext: IVssPageContext,
        selection: IListSelection,
        items: ObservableArray<IMenuItem>
    ): IHeaderCommandBarItem {
        const peoplePickerMenuItem: IHeaderCommandBarItem = {
            className: "people-picker-menu-item",
            id: "people-picker-menu-item-id",
            renderButton: props => {
                return <PeoplePicker key="people-picker-menu-button" selection={selection} items={items} />;
            },
            rank: 2,
            subtle: true,
            subMenuProps: {
                id: "submenu",
                items: items,
                onActivate: (menuItem: IMenuItem) => {
                    selection.select(items.value.indexOf(menuItem));
                    PeoplePicker.onActivate(menuItem, pageContext);
                }
            },
            text: Resources.PersonMenuLabel
        };
        return peoplePickerMenuItem;
    }
    private taskboardService: ITaskboardService;
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    public constructor(props: IPeoplePickerProps, context: IVssComponentContext) {
        super(props, context);

        this.taskboardService = this.context.pageContext.getService<ITaskboardService>("ITaskboardService");
        this.taskboardService.setEvaluatePeopleFilter(this.evaluatePeopleFilter);

        if (!props.items.value.length) {
            this.getSelectableValues();
        }
    }

    public componentDidMount(): void {
        if (this.props.items.value.length) {
            this.onChange();
        }
    }

    public render(): JSX.Element {
        return this.isNewSprintsViewEnabled ? (
            <Observer filteredItems={{ observableValue: this.taskboardService.getFilteredRows(), filter: this.onChange }}>
                {() => {
                    return (
                        <Dropdown
                            key={"people-picker-dropdown"}
                            items={this.props.items}
                            renderSelectedItems={this.renderSelectedItem}
                            onSelect={this.onChange}
                            renderExpandable={props => (
                                <DropdownExpandableButton
                                    key="people-picker-dropdown-expandable"
                                    {...props}
                                    className={css(props.className, "padding-left-4")}
                                />
                            )}
                            selection={this.props.selection}
                            showPrefix={false}
                            width={300}
                        />
                    );
                }}
            </Observer>
        ) : (
            <Observer filteredItems={{ observableValue: this.taskboardService.getFilteredRows(), filter: this.onChange }}>
                {() => {
                    return (
                        <Dropdown
                            key={"people-picker-dropdown"}
                            items={this.props.items}
                            renderSelectedItems={this.renderSelectedItem}
                            onSelect={this.onChange}
                            selection={this.props.selection}
                            showPrefix={false}
                            width={300}
                            role="menuitem"
                        />
                    );
                }}
            </Observer>
        );
    }

    private async getSelectableValues() {
        const filterDataSource = new BoardFilterDataSource(
            this.taskboardService,
            this.taskboardService.getTaskBoardData().value?.taskboardModel.filterableFieldNames ?? [],
            this.context.pageContext
        );

        const initialState = this.taskboardService.getTaskBoardData().value?.initialPeopleSwitcherState;
        const identityItems = await filterDataSource.getUniqueValues(CoreFieldRefNames.AssignedTo);

        // Default: select "All" item
        const macros = [AllFilterItem, MeFilterItem, UnassignedFilterItem];
        let selectedIndex: number | undefined = undefined;
        this.props.items.value = [
            ...macros,
            ...identityItems.map((item, index) => {
                const identityRef = convertPotentialIdentityRefFromFieldValue(this.context.pageContext, item, true) as IdentityRef;
                if (initialState && identityRef.uniqueName === initialState) {
                    selectedIndex = index + macros.length;
                }
                return this.createListBoxItem(identityRef);
            })
        ];

        if (selectedIndex === undefined) {
            switch (initialState) {
                case MeFilterItem.id:
                    selectedIndex = 1;
                    break;
                case UnassignedFilterItem.id:
                    selectedIndex = 2;
                    break;
                case AllFilterItem.id:
                default:
                    selectedIndex = 0;
                    break;
            }
        }

        this.props.selection.select(selectedIndex);

        this.onChange();
    }

    private createListBoxItem(identityRef: IdentityRef): IListBoxItem<IdentityRef> {
        return {
            id: identityRef.uniqueName,
            data: identityRef,
            text: identityRef.displayName,
            iconProps: {
                render: () => <IdentityView viewMode={IdentityDisplayMode.AvatarOnly} identityRef={identityRef} />
            }
        };
    }

    private static onActivate = (item: IMenuItem, pageContext: IVssPageContext, rows?: ITaskboardRow[]) => {
        const taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
        const teamId = taskboardService.getTaskBoardData().value?.taskboardModel.team.id ?? "";
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

        const pageService = pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        settingsService.setEntries({ [peopleSetting]: item.id }, SettingsUserScope.Me, SettingsConstants.WebTeam, teamId);
        let someRowsExpanded = false;
        const filteredRows = rows ?? taskboardService.getFilteredRows().value;

        // Expand/collapse the rows as necessary
        switch (item.id) {
            case AllFilterItem.id:
                filteredRows.forEach(row => {
                    // Don't expand completed work item parents
                    if (!(row.parent && taskboardService.isStateComplete(row.parent.value))) {
                        someRowsExpanded = true;
                        row.expanded.value = true;
                    }
                });
                taskboardService.getAllItems().forEach(item => {
                    item.value.grayedOut.value = false;
                });

                taskboardService.setSomeRowsExpanded(someRowsExpanded);
                break;
            case MeFilterItem.id:
            case UnassignedFilterItem.id:
            default:
                const currentUser = pageData?.user;
                const searchId =
                    item.id === UnassignedFilterItem.id
                        ? null
                        : getFriendlyDistinctDisplayName(item.id === MeFilterItem.id ? currentUser! : item.data!);
                filteredRows.forEach(row => {
                    const compareAssignedTo = (item?: IObservableClientWorkItem<ITaskboardWorkItem>, doNotGray?: boolean) => {
                        let assignedTo: string | IdentityRef | WorkItemIdentityRef | undefined | null = item?.value.getFieldValue(
                            CoreFieldRefNames.AssignedTo
                        );
                        if (typeof assignedTo === "string") {
                            assignedTo = convertPotentialIdentityRefFromFieldValue(pageContext, assignedTo, /** asIdentityRef */ true);
                        }
                        if (assignedTo && typeof assignedTo !== "string") {
                            if (isWorkItemIdentityRef(assignedTo)) {
                                assignedTo = assignedTo.distinctDisplayName;
                            } else {
                                assignedTo = getFriendlyDistinctDisplayName(assignedTo);
                            }
                        }
                        const isMatch = assignedTo === searchId || (searchId === null && assignedTo === undefined);
                        if (item && !isMatch && !doNotGray) {
                            item.value.grayedOut.value = true;
                        } else if (item && isMatch && !doNotGray) {
                            item.value.grayedOut.value = false;
                        }
                        return isMatch;
                    };
                    const parentMatch = compareAssignedTo(row.parent, true);
                    let anyChildMatch = false;
                    row.children.value.forEach(child => {
                        const result = compareAssignedTo(child);
                        if (result && !anyChildMatch) {
                            anyChildMatch = true;
                        }
                    });
                    if (
                        // Check if row.parent.id is less than 0. If so, it's a new item & we should keep it expanded
                        ((row.parent && row.parent.value.id < 0) || parentMatch || anyChildMatch) &&
                        !(row.parent && taskboardService.isStateComplete(row.parent.value))
                    ) {
                        row.expanded.value = true;
                        someRowsExpanded = true;
                    } else {
                        row.expanded.value = false;
                    }
                });

                taskboardService.setSomeRowsExpanded(someRowsExpanded);
                break;
        }
    };

    private evaluatePeopleFilter = (rows?: ITaskboardRow[]): boolean => {
        if (!this.props.selection.value.length || !this.props.selection.value[0]) {
            return false;
        }
        const selectedIndex = this.props.selection.value[0].beginIndex;
        const item = this.props.items.value[selectedIndex];
        PeoplePicker.onActivate(item, this.context.pageContext, rows);
        return true;
    };

    private onChange = () => {
        return this.evaluatePeopleFilter();
    };

    private renderSelectedItem = (selection: IListSelection, items: IListBoxItem<{}>[] | IMenuItem[]) => {
        if (!items?.length) {
            return "";
        }
        const selectedIndex = selection.value[0].beginIndex;
        return format(Resources.PersonLabel, items[selectedIndex]?.text);
    };
}

Components.add("people-picker", PeoplePicker);
